import React, { useState, useEffect } from 'react';
import { Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

interface ConcreteState {
  length: string;
  width: string;
  height: string;
  shape: 'rectangular' | 'circular' | 'cylindrical';
  diameter?: string;
  quantity: string;
  wastage: string;
}

function KalkulatorBetona() {
  const [values, setValues] = useState<ConcreteState>({
    length: '',
    width: '',
    height: '',
    shape: 'rectangular',
    diameter: '',
    quantity: '1',
    wastage: '10'
  });

  const [results, setResults] = useState<{
    volume: number;
    totalVolume: number;
    cement: number;
    sand: number;
    aggregate: number;
    water: number;
  } | null>(null);

  const calculateVolume = () => {
    let baseVolume = 0;
    
    if (values.shape === 'rectangular') {
      const length = parseFloat(values.length);
      const width = parseFloat(values.width);
      const height = parseFloat(values.height);
      
      if (isNaN(length) || isNaN(width) || isNaN(height)) return null;
      baseVolume = length * width * height;
    } else if (values.shape === 'circular' || values.shape === 'cylindrical') {
      const diameter = parseFloat(values.diameter || '0');
      const height = parseFloat(values.height);
      
      if (isNaN(diameter) || isNaN(height)) return null;
      const radius = diameter / 2;
      baseVolume = Math.PI * radius * radius * height;
    }

    const quantity = parseInt(values.quantity) || 1;
    const wastage = parseFloat(values.wastage) || 0;
    const totalVolume = baseVolume * quantity * (1 + wastage / 100);

    // Standard concrete mix ratio 1:2:4
    // Cement : Sand : Aggregate : Water
    // 1 : 2 : 4 : 0.5
    const totalMix = 1 + 2 + 4; // 7 parts total
    const cement = (totalVolume / totalMix) * 1;
    const sand = (totalVolume / totalMix) * 2;
    const aggregate = (totalVolume / totalMix) * 4;
    const water = cement * 0.5; // Water is typically 0.5 times cement

    return {
      volume: parseFloat(baseVolume.toFixed(3)),
      totalVolume: parseFloat(totalVolume.toFixed(3)),
      cement: parseFloat(cement.toFixed(3)),
      sand: parseFloat(sand.toFixed(3)),
      aggregate: parseFloat(aggregate.toFixed(3)),
      water: parseFloat(water.toFixed(3))
    };
  };

  const handleInputChange = (field: keyof ConcreteState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    const results = calculateVolume();
    if (results) {
      setResults(results);
    }
  }, [values]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Betona",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte potrebnu količinu betona i materijala za vaš građevinski projekt. Precizni izračuni za različite oblike i dimenzije.",
    "url": "https://online-kalkulator.com/kalkulator-betona",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Kalkulator Betona | Izračun Količine Betona"
        description="Izračunajte potrebnu količinu betona i materijala za vaš građevinski projekt. Precizni izračuni za različite oblike i dimenzije."
        canonicalUrl="/kalkulator-betona"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Betona</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Shape Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Oblik konstrukcije
                </label>
                <select
                  value={values.shape}
                  onChange={(e) => handleInputChange('shape', e.target.value as ConcreteState['shape'])}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="rectangular">Pravokutni (Temelj, Ploča)</option>
                  <option value="circular">Kružni (Temelj)</option>
                  <option value="cylindrical">Cilindrični (Stup)</option>
                </select>
              </div>

              {/* Dimensions */}
              <div className="grid grid-cols-2 gap-4">
                {values.shape === 'rectangular' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dužina (m)
                      </label>
                      <input
                        type="number"
                        value={values.length}
                        onChange={(e) => handleInputChange('length', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="npr. 5"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Širina (m)
                      </label>
                      <input
                        type="number"
                        value={values.width}
                        onChange={(e) => handleInputChange('width', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="npr. 3"
                        step="0.01"
                      />
                    </div>
                  </>
                ) : (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Promjer (m)
                    </label>
                    <input
                      type="number"
                      value={values.diameter}
                      onChange={(e) => handleInputChange('diameter', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="npr. 0.3"
                      step="0.01"
                    />
                  </div>
                )}

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Visina/Debljina (m)
                  </label>
                  <input
                    type="number"
                    value={values.height}
                    onChange={(e) => handleInputChange('height', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 0.2"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Additional Options */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Količina
                  </label>
                  <input
                    type="number"
                    value={values.quantity}
                    onChange={(e) => handleInputChange('quantity', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 1"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dodatak za rastur (%)
                  </label>
                  <input
                    type="number"
                    value={values.wastage}
                    onChange={(e) => handleInputChange('wastage', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 10"
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600 mb-1">Ukupni volumen betona</div>
                      <div className="text-2xl font-bold text-blue-600">
                        {results.totalVolume} m³
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600 mb-1">Cement</div>
                      <div className="text-lg font-semibold">{results.cement} m³</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600 mb-1">Pijesak</div>
                      <div className="text-lg font-semibold">{results.sand} m³</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600 mb-1">Šljunak</div>
                      <div className="text-lg font-semibold">{results.aggregate} m³</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-sm text-gray-600 mb-1">Voda</div>
                      <div className="text-lg font-semibold">{results.water} m³</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Betona</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator betona je koristan alat koji vam pomaže izračunati potrebnu količinu
                  betona i materijala za vaš građevinski projekt. Kalkulator uzima u obzir dimenzije
                  konstrukcije, oblik, količinu i dodatak za rastur materijala.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Podržani Oblici</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Pravokutni (za temelje, ploče, zidove)</li>
                  <li>Kružni (za kružne temelje)</li>
                  <li>Cilindrični (za stupove)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Omjer Mješavine</h2>
                <p className="text-gray-600 mb-4">
                  Kalkulator koristi standardni omjer mješavine betona 1:2:4:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>1 dio cementa</li>
                  <li>2 dijela pijeska</li>
                  <li>4 dijela šljunka</li>
                  <li>0.5 dijelova vode (u odnosu na cement)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Odaberite oblik konstrukcije</li>
                  <li>Unesite potrebne dimenzije u metrima</li>
                  <li>Odredite količinu istih elemenata</li>
                  <li>Postavite postotak dodatka za rastur (obično 10%)</li>
                  <li>Rezultati će se automatski izračunati</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Dodatak za Rastur</h2>
                <p className="text-gray-600 mb-4">
                  Dodatak za rastur je važan faktor koji uzima u obzir:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Gubitke tijekom miješanja i transporta</li>
                  <li>Neravnine na površini</li>
                  <li>Moguće greške u mjerenju</li>
                  <li>Preporučeni dodatak je 10%</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Sve dimenzije unosite u metrima</li>
                  <li>Rezultati su prikazani u kubnim metrima (m³)</li>
                  <li>Izračuni su približni i mogu odstupati od stvarnih potreba</li>
                  <li>Za velike projekte konzultirajte građevinskog stručnjaka</li>
                  <li>Uzmite u obzir lokalne građevinske propise</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Betoniranje</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Pripremite površinu prije betoniranja</li>
                  <li>Koristite kvalitetne materijale</li>
                  <li>Osigurajte pravilno miješanje betona</li>
                  <li>Zaštitite beton tijekom sušenja</li>
                  <li>Održavajte vlažnost betona tijekom prvih dana</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorBetona;